// aHedge investor (customer) shell — the read-only consumer dashboard.
// One scrolling page; the sidebar scrolls to sections (anchor nav) and
// scroll-spies the active one. Dark branded sidebar + light canvas, no
// ops controls. Rendered for the `investor` role (and admin preview).
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { LayoutDashboard, LineChart, Layers, Wallet } from 'lucide-react';
import { cn, formatDuration } from '../../lib/utils';
import { usePortfolioSummary } from '../../hooks/useApi';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'performance', label: 'Performance', icon: LineChart },
  { id: 'positions', label: 'Positions', icon: Layers },
  { id: 'pnl', label: 'Total P&L', icon: Wallet },
];

function LiveBadge() {
  const { data } = usePortfolioSummary();
  const ago = data?.lastHedgeTs != null ? formatDuration(Date.now() - data.lastHedgeTs) : null;
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600 shadow-sm">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      {ago ? <>Live · last hedge {ago} ago</> : <>Live</>}
    </div>
  );
}

export function InvestorShell({ children }: { children: ReactNode }) {
  const mainRef = useRef<HTMLElement>(null);
  const [active, setActive] = useState('overview');

  // Scroll-spy: highlight the section nearest the top of the scroll area.
  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => el !== null,
    );
    if (els.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { root, rootMargin: '-10% 0px -70% 0px', threshold: 0 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const go = (id: string) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 text-slate-900">
      {/* Sidebar */}
      <aside className="flex w-60 shrink-0 flex-col bg-slate-900 text-slate-300">
        <div className="flex h-16 items-center gap-3 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-sm font-bold text-white">
            aH
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">aHedge</p>
            <p className="truncate text-xs text-slate-400">Auto-hedged LP</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3 py-4">
          {SECTIONS.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-16 shrink-0 items-center justify-end border-b border-slate-200 bg-white px-8">
          <LiveBadge />
        </header>
        <main ref={mainRef} className="flex-1 overflow-y-auto px-8 py-8">
          <div className="mx-auto max-w-6xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
